import type React from "react";
import "./CurrentTenantPaymentList.css";
import {
  ContractorService,
  PaymentDueAccountVM,
  PaymentMethodVM,
  PaymentService,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import SelectField from "../form-fields/SelectField";
import InputField from "../form-fields/InputField";
import { UserLoginData } from "../../components/common/charts/Enums";
import ActionMenu from "../actionMenus/action-menu";
import { utcDateFormat } from "../../utils/helper";
import DateHelper from "../../utils/DateHelper";
import UtilHelper from "../../utils/UtilHelper";
import H2 from "../typography/H2";
import SimpleSpinner from "../../components/loader";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import { GetImages } from "../../utils/GetImages";
import EmptyState from "../emptystate";
import BluePayableIcon from "../../assets/Payment-Icon.svg";

export interface Payment {
  id?: number;
  payerPayeeName?: string | null;
  description?: string | null;
  dueDate?: string;
  amount?: number;
  streetAddress?: string | null;
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: number;
  className?: string;
  detailsActiveTab?: string;

  refreshTrigger?: number;
  onRefresh?: () => void;

  expensesDueRaw?: number | string;
  expensesDue?: number | string;
}

const ContractorPaymentList: React.FC<Payment> = ({
  isEntity,
  entityId,
  entityTypeId,
  className,
  detailsActiveTab,
  refreshTrigger,
  onRefresh,
  expensesDue,
  expensesDueRaw
}) => {
  const { id: contractorId } = useParams();
  const [payments, setPayments] = useState<PaymentDueAccountVM[]>([]);
  const [paymentModes, setPaymentModes] = useState<PaymentMethodVM[]>([]);
  const [selectOptions, setSelectOptions] = useState<
    { Id: number; Name: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [contractorData, setContractorData] = useState<any>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentDueAccountVM | null>(null);
  const [isBulk, setIsBulk] = useState(false);

  const currencySymbol = useCurrencySymbol();
  const methods = useForm({
    defaultValues: {
      PaymentModeId: "",
      date: "",
      checkNo: "",
      transactionId: "",
    },
  });
  const navigate = useNavigate();
  const formId = "post-payment-form";

  const selectedMode = useWatch({ control: methods.control, name: "PaymentModeId" });

  // Derived flags based on selected payment method type (same logic as ServiceRequestDetails)
  const selectedPaymentMethod = paymentModes.find((m) => String(m.id) === String(selectedMode));
  const isCheck = selectedPaymentMethod?.formatted?.PaymentMethodTypeId === "Check";
  const showTransactionId =
    selectedPaymentMethod?.formatted?.PaymentMethodTypeId !== "Check" &&
    selectedPaymentMethod?.formatted?.PaymentMethodTypeId !== "Cash";



  const {
    formState: { isDirty, isSubmitting },
  } = methods;

  // Menu actions - Only Post & Delete
  const getMenuItems = (payment: PaymentDueAccountVM) => [
    {
      icon: BluePayableIcon,
      label: "Post Payment",
      action: () => {
        setSelectedPayment(payment);
        setModalOpen(true);
      },
      isDivider: false,
      isDestructive: false
    },
    { isDivider: true },
    {
      icon: deleteIcon,
      label: "Delete",
      action: () => {
        setSelectedPayment(payment);
        setShowConfirmation("delete");
      },
      isDivider: false,
      isDestructive: false
    },
  ];


  // Reset conditional fields when payment mode changes
  useEffect(() => {
    methods.resetField("checkNo");
    methods.resetField("transactionId");
  }, [selectedMode]);
  const deletePayment = async () => {
    if (!selectedPayment?.id) return;
    try {
      await apiHelper(
        PaymentService.deleteApiPaymentDueAccount,
        {
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
          showNotification: true,
          successMessage: "Payment Deleted Successfully",
        },
        selectedPayment.id
      );
      getPayments();
      setShowConfirmation(null);
      setSelectedPayment(null);

      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Delete Payment Error:", err);
      setShowConfirmation(null);
    }
  };

  const fetchCurrentContractorDetails = () => {
    if (contractorId) {
      apiHelper(ContractorService.getApiContractor, { showNotification: false }, contractorId)
        .then((res) => {
          if (res?.data?.success) {
            setContractorData(res.data.success);
          }
        })
        .catch((err) => {
          console.error("Error fetching contractor:", err);
        });
    }
  };

  const getPaymentsMode = async () => {
    setLoading(true);
    try {
      const orgId = parseInt(
        sessionStorage.getItem(UserLoginData.organization_id) || "0"
      );
      const res = await apiHelper(
        PaymentService.getApiPaymentMethodOrganizationlist,
        { showNotification: false },
        orgId
      );
      const rawItems = res?.data?.success || [];
      if (rawItems.length) {
        setPaymentModes(rawItems);
        setSelectOptions(
          rawItems.map((item) => ({
            Id: item.id!,
            Name: item.accountName,
          }))
        );
      } else {
        setPaymentModes([]);
        setSelectOptions([]);
      }
    } catch (err) {
      console.error("Payments API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getPayments = async () => {
    if (!contractorId) return;
    setLoading(true);
    try {
      const res = await apiHelper(
        PaymentService.getApiPaymentDueAccountContractorlist,
        { showNotification: false },
        contractorId
      );
      const rawItems = res?.data?.success || [];



      if (rawItems.length) {
        setPayments(rawItems);



      } else {
        setPayments([]);
      }
    } catch (err) {
      console.error("Contractor Payments API Error:", err);
    } finally {
      setLoading(false);
    }
  };


  const handleSinglePaymentOpen = (payment: PaymentDueAccountVM) => {
    setIsBulk(false);
    setSelectedPayment(payment);
    setModalOpen(true);
  };

  const getNumericAmount = (amount: any): number => {
    if (typeof amount === 'number') return amount;
    if (typeof amount === 'string') {
      // Remove currency symbols and commas, then parse
      return parseFloat(amount.replace(/[^0-9.-]+/g, "")) || 0;
    }
    return 0;
  };

  const postPayments = async (formData: any) => {
    setLoading(true);
    try {
      const paymentDate = utcDateFormat(formData.date);
      const paymentMethodId = Number(formData.PaymentModeId);

      if (isBulk) {
        // --- LOOP LOGIC FOR BULK PAYMENTS ---
        for (const payment of payments) {
          const payload = {
            payDueAccountId: Number(payment.id),
            // Yahan hum formatted amount ke bajaye raw amount use karenge agar available ho
            amount: payment.amount,
            fromPaymentMethodId: paymentMethodId,
            paymentDate: paymentDate,
            transactionId: crypto.randomUUID().replaceAll("-", "").toUpperCase(),
          };

          await apiHelper(
            PaymentService.putApiPaymentDueAccountPay,
            { showNotification: false },
            payload
          );
        }
        // Success notification for all
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: "All payments posted successfully.",
          type: NotificationType.Success,
          icon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
        });
      } else {
        // --- SINGLE PAYMENT LOGIC ---
        if (!selectedPayment) return;
        const payload = {
          payDueAccountId: Number(selectedPayment.id),
          amount: selectedPayment.amount,
          fromPaymentMethodId: paymentMethodId,
          paymentDate: paymentDate,
          checkNumber: formData.checkNo || "",
          transactionId: (isCheck || selectedPaymentMethod?.formatted?.PaymentMethodTypeId === "Cash")
            ? crypto.randomUUID().replaceAll("-", "").toUpperCase()
            : formData.transactionId || crypto.randomUUID().replaceAll("-", "").toUpperCase(),
        };
        await apiHelper(
          PaymentService.putApiPaymentDueAccountPay,
          { showNotification: true, 
            successMessage: "Payment Posted Successfully", 
            successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />, 
          },
          payload
        );
      }

      getPayments();
      setModalOpen(false);
      setIsBulk(false);
      setSelectedPayment(null);

      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Post Payment Error:", err);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    if (contractorId) {
      getPayments();
      getPaymentsMode();
      fetchCurrentContractorDetails();
    }
  }, [contractorId]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      getPayments();
    }
  }, [refreshTrigger]);


 if (loading) {
    return (
     <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }

  if (payments.length === 0) {
    return (
      <div className="content-wrapper container-fluid">
        <EmptyState
          icon={GetImages.emptyStatePayment}
          heading="No Payments Found"
          isButton={false}
        />
      </div>

    );
  }

  return (
    <>
        <div className="payments-content">
          <div className="payments-header">
            <H2
              text="Payments"
            />
            {/* <SimpleButton
              label="Add Payment"
              onClick={() => {
                navigate("/payment/create", {
                  state: {
                    isEntityProp: isEntity,
                    entityIdProp: entityId,
                    entityTypeIdProp: entityTypeId,
                    from: window.location.pathname,
                    activeTab: detailsActiveTab,
                  },
                });
              }}
            /> */}
          </div>
          <div className="table-container">

            <table className="payments-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Description</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payments.length ? (
                  payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{payment.streetAddress || "-"}</td>
                      <td>{payment.description || "-"}</td>
                      <td>
                        {DateHelper.formatISOToMMDDYYYY(payment.dueDate) || "-"}
                      </td>
                      <td>
                        <CurrencyTooltip
                          content={payment.formatted?.AmountRaw || 0}
                          formatted={payment.formatted?.Amount}
                        >
                          <span>
                            {payment.formatted?.Amount ||
                              "-"}
                          </span>
                        </CurrencyTooltip>
                      </td>
                      <td>
                        <ActionMenu
                          menuItems={getMenuItems(payment)}
                          trigger={
                            <button
                              className="action-button"
                              aria-label="More options"
                            >
                              <img
                                src={dropdownIcon}
                                alt="More options"
                                width={20}
                                height={20}
                                className="action-icon"
                              />
                            </button>
                          }
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      No payments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

          </div>

          {
            payments.length > 0 && (
              <div className="total-amount-card">
                <div className="total-amount-content" style={{ justifyContent: "space-between" }}>
                  <div>
                    <span className="total-label">Total Amount Due</span>
                  </div>



                  <CurrencyTooltip
                    content={expensesDueRaw}
                    formatted={expensesDue}
                  >
                    <span className="total-value">{expensesDue}</span>
                  </CurrencyTooltip>
                </div>
              </div>
            )
          }


        </div>
     

      {/* Delete Confirmation */}
      {showConfirmation === "delete" && selectedPayment && (
        <ConfirmationPopup
          isOpen={true}
          title="Delete Payment"
          message={`Do you want to delete this payment of ${currencySymbol}${selectedPayment?.amount?.toFixed(2)}?`}
          primaryButtonLabel="Delete"
          secondaryButtonLabel="Cancel"
          onPrimaryAction={deletePayment}
          onSecondaryAction={() => setShowConfirmation(null)}
          onClose={() => setShowConfirmation(null)}
          isPrimaryDestructive={true}
        />
      )}

      {/* Post Payment Modal */}
      {isModalOpen && (
        <RightSideModal
          isOpen={isModalOpen}
          onClose={() => { setModalOpen(false); setIsBulk(false); }}
          title={isBulk ? "Post All Payments" : "Post Payment"}
          buttonlabel="Post"
          formId={formId}
          maxWidth={550}
        >
          <Card className="no-bg">




            <div className="tenant-info">
              {contractorData && selectedPayment?.amount && (
                <>
                  <div className="tenant-left">
                    <div className="tenant-avatar">
                      <div className="tenant-initials">
                        {UtilHelper.getInitials(
                          contractorData?.firstName + " " + contractorData?.lastName
                        )}
                      </div>
                    </div>
                    <div className="tenant-name">
                      {contractorData?.firstName + " " + contractorData?.lastName}
                      <p className="tenant-payment-type">  {isBulk ? `Contractor Payments` : "Contractor Payment"}</p>
                    </div>
                  </div>
                  <div className="tenant-amount">
                    {currencySymbol}{getNumericAmount(selectedPayment?.amount).toFixed(2)}
                  </div>
                </>
              )}
            </div>

            <FormProvider {...methods}>
              <form id={formId} onSubmit={methods.handleSubmit(postPayments)}>
                <div className="row">
                  <div className="col-md-6">
                    <SelectField
                      name="PaymentModeId"
                      label="Payment Mode"
                      required
                      requiredMessage="Payment mode is required"
                      options={selectOptions}
                      placeholderText="Select Mode"
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField name="date" label="Payment Date" type="date" required requiredMessage="Payment Date is required" />
                  </div>
                </div>

                {isCheck && (
                  <InputField
                    name="checkNo"
                    label="Enter Check No"
                    type="text"
                    required={true}
                    requiredMessage="Please enter a check number"
                    placeholder="Enter check number"
                    onlyNumeric={true}
                  />
                )}

                {showTransactionId && (
                  <InputField
                    name="transactionId"
                    label="Transaction ID"
                    type="text"
                    required={true}
                    requiredMessage="Please enter a transaction ID"
                    placeholder="Enter transaction ID"
                  />
                )}
              </form>
              <div className="payment-alert">
                <p>Payment Amount:</p>
                <p className="payment-divider">------------------------</p>
                <p style={{ fontWeight: "bold" }}>
                  {currencySymbol}{getNumericAmount(selectedPayment?.amount).toFixed(2)}
                </p>
              </div>
            </FormProvider>
          </Card>
        </RightSideModal>
      )}
    </>
  );
};

export default ContractorPaymentList;